// src/pages/GLBAPrivacyNotice.tsx

import React, { useEffect, useState } from 'react';
import { auth, db } from '../firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import Navigation from '../components/Navigation';
import { markInitialNoticeDelivered } from '../lib/glba/glbaPreferencesApi';
import { sendGLBAPrivacyNotice } from '../lib/email/emailService';

const GLBAPrivacyNotice: React.FC = () => {
  const [userEmail, setUserEmail] = useState('');
  const [hasAcknowledged, setHasAcknowledged] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadUserData = async () => {
      const user = auth.currentUser;
      if (user) {
        setUserEmail(user.email || '');
        
        // Check if user has already acknowledged the initial notice
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const glbaPrefsDoc = await getDoc(doc(db, 'glbaPreferences', user.uid));
            if (glbaPrefsDoc.exists() && glbaPrefsDoc.data().initialNoticeDeliveredAt) {
              setHasAcknowledged(true);
            }
          }
        } catch (error) {
          console.error('Error checking notice status:', error);
        }
      }
      setIsLoading(false);
    };

    loadUserData();
  }, []);

  const handleAcknowledge = async () => {
    const user = auth.currentUser;
    if (!user) {
      alert('You must be signed in to acknowledge this notice.');
      return;
    }

    try {
      // Mark initial notice as delivered
      await markInitialNoticeDelivered(user.uid);
      
      // Send email confirmation (don't wait for it, don't fail if it errors)
      sendGLBAPrivacyNotice(user.email || userEmail, 'initial').catch((err) => {
        console.error('Failed to send GLBA notice email:', err);
        // Don't block acknowledgment if email fails
      });
      
      setHasAcknowledged(true);
    } catch (error: any) {
      console.error('Error acknowledging notice:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      alert(`Error saving acknowledgment: ${error.message || 'Please try again.'}`);
    }
  };

  if (isLoading) {
    return (
      <div>
        <Navigation />
        <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>
      </div>
    );
  }

  return (
    <div>
      <Navigation />
      <div style={{ padding: '2rem', maxWidth: '900px', margin: '0 auto', lineHeight: '1.6' }}>
        <h1 style={{ marginBottom: '1rem' }}>Financial Privacy Notice</h1>
        <p style={{ color: '#666', marginBottom: '2rem', fontSize: '0.875rem' }}>
          <strong>Last Updated:</strong> January 21, 2025
        </p>

        <div style={{
          backgroundColor: '#fff3cd',
          border: '1px solid #ffc107',
          padding: '1rem',
          borderRadius: '4px',
          marginBottom: '2rem'
        }}>
          <p style={{ margin: 0, fontWeight: 'bold' }}>
            This notice is provided in accordance with the Gramm-Leach-Bliley Act (GLBA) and describes 
            our privacy practices regarding nonpublic personal information.
          </p>
        </div>

        <section style={{ marginBottom: '2rem' }}>
          <h2>What We Do</h2>
          <p>
            Help Keep My Money LLC is committed to protecting the privacy and confidentiality of your 
            nonpublic personal information ("NPI"). This notice describes our privacy practices and 
            your rights regarding your financial information.
          </p>
        </section>

        <section style={{ marginBottom: '2rem' }}>
          <h2>Information We Collect</h2>
          <p>We collect nonpublic personal information about you from the following sources:</p>
          <ul>
            <li><strong>Information you provide:</strong> Applications, forms, and other information you 
              provide to us, including your name, address, phone number, email, Social Security Number, 
              EIN, financial information, and business details.</li>
            <li><strong>Information about your transactions:</strong> Information about your transactions 
              with us, our affiliates, or others, including account balances, payment history, and 
              service usage.</li>
            <li><strong>Information from third parties:</strong> Information we receive from credit bureaus, 
              financial institutions, and other third parties to verify your identity and assess your 
              creditworthiness.</li>
          </ul>
        </section>

        <section style={{ marginBottom: '2rem' }}>
          <h2>Information We Disclose</h2>
          <p>
            We may disclose nonpublic personal information about you to the following types of third parties:
          </p>
          
          <h3 style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>Service Providers</h3>
          <p>
            We may disclose your information to companies that perform services on our behalf, such as:
          </p>
          <ul>
            <li>Payment processors (Stripe, PayPal)</li>
            <li>Email and communication services (Resend, Twilio)</li>
            <li>Cloud storage and hosting providers (Firebase, Vercel, Bluehost)</li>
            <li>Technology service providers</li>
          </ul>
          <p>
            These companies are contractually obligated to protect your information and use it solely 
            for the purposes we specify.
          </p>

          <h3 style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>As Required by Law</h3>
          <p>
            We may disclose your information as required or permitted by law, including to:
          </p>
          <ul>
            <li>Government agencies and regulators</li>
            <li>Law enforcement officials</li>
            <li>Courts and legal process</li>
            <li>To protect our rights and property</li>
          </ul>

          <h3 style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>Affiliates</h3>
          <p>
            We may share information with our affiliates for business purposes, such as providing 
            integrated services and improving our offerings.
          </p>
        </section>

        <section style={{ marginBottom: '2rem' }}>
          <h2>Your Right to Opt-Out</h2>
          <p>
            Federal law gives you the right to limit some, but not all, sharing of your nonpublic 
            personal information. You have the right to opt-out of:
          </p>
          <ul>
            <li><strong>Sharing with nonaffiliated third parties</strong> for their marketing purposes</li>
            <li><strong>Sharing with affiliates</strong> for their marketing purposes</li>
          </ul>
          <p>
            <strong>Note:</strong> You cannot opt-out of sharing that is necessary for us to provide 
            our services (e.g., with payment processors, email services) or that is required by law.
          </p>
        </section>

        <section style={{ marginBottom: '2rem' }}>
          <h2>How We Protect Your Information</h2>
          <p>
            We maintain physical, electronic, and procedural safeguards that comply with federal standards 
            to protect your nonpublic personal information. These safeguards include:
          </p>
          <ul>
            <li>Encryption of data in transit (SSL/TLS) and at rest (AES-256)</li>
            <li>Access controls that restrict access to your information to employees who need it to 
              provide services to you</li>
            <li>Regular security assessments and updates</li>
            <li>Employee training on privacy and security</li>
            <li>Secure disposal of information when no longer needed</li>
          </ul>
        </section>

        <section style={{ marginBottom: '2rem' }}>
          <h2>How to Exercise Your Opt-Out Rights</h2>
          <p>You can opt-out of information sharing by:</p>
          <ol>
            <li>
              <strong>Online:</strong> Visit your{' '}
              <a href="/glba-preferences" style={{ color: '#007bff', textDecoration: 'none' }}>
                Financial Privacy Preferences
              </a>{' '}
              page to update your preferences.
            </li>
            <li>
              <strong>By Email:</strong> Send an email to info@helpkeepmymoney.com with "GLBA Opt-Out" 
              in the subject line and include your name and account email.
            </li>
            <li>
              <strong>By Phone:</strong> Call us at (661) 524-6674.
            </li>
            <li>
              <strong>By Mail:</strong> Send a written request to:
              <div style={{
                backgroundColor: '#f8f9fa',
                padding: '1rem',
                borderRadius: '4px',
                marginTop: '0.5rem'
              }}>
                Help Keep My Money LLC<br />
                1752 E. Ave. J, Suite 214<br />
                Lancaster, CA 93535
              </div>
            </li>
          </ol>
          <p>
            <strong>Processing Time:</strong> Your opt-out request will be processed within 30 days 
            of receipt. You will receive confirmation once your preferences have been updated.
          </p>
        </section>

        <section style={{ marginBottom: '2rem' }}>
          <h2>Annual Privacy Notices</h2>
          <p>
            We will provide you with an annual privacy notice each year. You can also access this 
            notice at any time by visiting this page or contacting us.
          </p>
        </section>

        <section style={{ marginBottom: '2rem' }}>
          <h2>Questions?</h2>
          <p>If you have questions about this privacy notice, please contact us:</p>
          <div style={{
            backgroundColor: '#f8f9fa',
            padding: '1rem',
            borderRadius: '4px',
            marginTop: '0.5rem'
          }}>
            <p style={{ margin: 0 }}>
              <strong>Help Keep My Money LLC</strong><br />
              Email: info@helpkeepmymoney.com<br />
              Phone: (661) 524-6674<br />
              Address: 1752 E. Ave. J, Suite 214, Lancaster, CA 93535
            </p>
          </div>
        </section>

        {!hasAcknowledged && (
          <div style={{
            marginTop: '3rem',
            padding: '1.5rem',
            backgroundColor: '#e7f3ff',
            border: '1px solid #007bff',
            borderRadius: '4px'
          }}>
            <h3 style={{ marginTop: 0 }}>Acknowledgment</h3>
            <p>
              By clicking the button below, you acknowledge that you have received and reviewed this 
              Financial Privacy Notice.
            </p>
            <button
              onClick={handleAcknowledge}
              style={{
                backgroundColor: '#007bff',
                color: 'white',
                padding: '10px 20px',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 'bold',
                marginTop: '1rem'
              }}
            >
              I Acknowledge Receipt of This Notice
            </button>
          </div>
        )}

        {hasAcknowledged && (
          <div style={{
            marginTop: '3rem',
            padding: '1rem',
            backgroundColor: '#d4edda',
            border: '1px solid #28a745',
            borderRadius: '4px',
            color: '#155724'
          }}>
            <p style={{ margin: 0, fontWeight: 'bold' }}>
              ✓ You have acknowledged receipt of this Financial Privacy Notice.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default GLBAPrivacyNotice;
